import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ConfirmationAlertDialogComponent } from 'app/modules/settings/components/confirmation-alert-dialog/confirmation-alert-dialog.component';
import { UserCreateModel } from 'app/modules/settings/interfaces/user';
import { UserPasswordResetRequest } from '../../classes/user-password-reset';
import { Employee } from '../../interfaces/client-data';
import { ToastService } from '../../services/toast.service';
import { UserService } from '../../services/user.service';

import { BulkUserActions } from 'app/modules/shared/classes/reusable-functions';

@Component({
    selector: 'app-user-actions-dialog',
    templateUrl: './user-actions-dialog.component.html',
    styleUrls: ['./user-actions-dialog.component.scss'],
})
export class UserActionsDialogComponent implements OnInit {
    action: BulkUserActions;
    employees: Employee[];
    welcomeEmail: boolean = false;
    userCount: number = 0;
    employeeText: string = 'Employees';
    userText: string = 'Users';
    actionText: string = 'create';
    actionTextProper: string = 'Create';
    actionTextPastTense: string = 'created';
    dialogTitle: string = '';
    showWelcomeEmailCheck: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        public router: Router,
        public userActionsDialog: MatDialogRef<UserActionsDialogComponent>,
        private userService: UserService,
        public dialog: MatDialog,
        private toast: ToastService
    ) {}

    ngOnInit() {
        this.action = this.data.action;
        this.filterEmployees();
    }

    filterEmployees() {
        switch (this.action) {
            case BulkUserActions.ConvertFromEmployee:
                this.employees = this.data.employees.filter(
                    (emp) => !emp.userGUID && !emp.terminationDate
                );
                for (let employee of this.employees) {
                    employee.convertToUser = true;
                }
                this.showWelcomeEmailCheck = true;
                this.userCount = this.employees.length;

                this.updateLabels();
                break;
            case BulkUserActions.ResendWelcomeEmail:
                break;
        }
    }

    /* Convert employees to users in bulk */
    convertEmployees() {
        let message: string = `Are you sure you want to ${this.actionText} ${
            this.userCount
        } ${this.userText.toLowerCase()}?`;
        if (this.welcomeEmail) {
            message = `Are you sure you want to ${this.actionText} ${
                this.userCount
            } ${this.userText.toLowerCase()} and send them a welcome email?`;
        }

        const dialogRef = this.dialog.open(ConfirmationAlertDialogComponent, {
            data: {
                title: `${this.actionTextProper} ${this.userText}?`,
                message: message,
            },
        });

        this.userActionsDialog.close();

        dialogRef
            .afterClosed()
            .toPromise()
            .then((result) => {
                this.convertEmployeesAction(result);
                return result;
            });
    }

    convertEmployeesAction(result: any) {
        if (result === 'accept') {
            let employees: Employee[] = this.employees;
            let users: UserCreateModel[] = [];

            for (let employee of employees) {
                if (!employee.userGUID && employee.convertToUser) {
                    let user: UserCreateModel = {
                        loginEmail: employee.loginEmail,
                        clientShortName: employee.clientShortName,
                        employeeIdentifier: employee.primaryIdentifier,
                        positionName: employee.positionName,
                    };

                    users.push(user);
                }
            }

            this.userService.createBulkUsers(users).subscribe((data) => {
                if (this.welcomeEmail) {
                    let emails: UserPasswordResetRequest[] = [];
                    for (let loginEmail of data) {
                        emails.push({
                            username: loginEmail,
                        });
                    }

                    this.userService.sendWelcomeEmail(emails).subscribe(
                        (data) => {
                            this.toast.success(
                                `Successfully ${
                                    this.actionTextPastTense
                                } and sent welcome emails for ${
                                    this.userCount
                                } ${this.userText.toLowerCase()}!`
                            );
                            this.reloadComponentOnEditAndSave();
                        },
                        (error) => {
                            throw new Error(error);
                        }
                    );
                } else {
                    this.toast.success(
                        `Successfully ${this.actionTextPastTense} ${
                            this.userCount
                        } ${this.userText.toLowerCase()}!`
                    );
                    this.reloadComponentOnEditAndSave();
                }
            });
        } else if (result === 'close') {
            const actionDialogRef = this.dialog.open(UserActionsDialogComponent, {
                data: { employees: this.employees, action: BulkUserActions.ConvertFromEmployee },
            });
        }
    }

    labelClick(employeeToggle: any, emp: any) {
        employeeToggle.toggle();
        emp['convertToUser'] = !emp['convertToUser'];
        this.updateUserCount(employeeToggle);
    }

    checkChange(employeeToggle: any) {
        this.updateUserCount(employeeToggle);
    }

    updateLabels(): void {
        if (this.userCount == 1) {
            this.employeeText = 'Employee';
            this.userText = 'User';
        } else {
            this.employeeText = 'Employees';
            this.userText = 'Users';
        }

        switch (this.action) {
            case BulkUserActions.ConvertFromEmployee:
                this.actionText = 'create';
                this.actionTextProper = 'Create';
                this.actionTextPastTense = 'created';
                this.dialogTitle = `Create user ${this.userCount <= 1 ? 'account' : 'accounts'}`;
                break;
        }
    }

    updateUserCount(employee: any) {
        if (employee.checked) {
            this.userCount += 1;
        } else {
            this.userCount -= 1;
        }

        this.updateLabels();
    }

    reloadComponentOnEditAndSave() {
        let currentUrl = this.router.url;
        this.router.navigate(['']).then(() => {
            this.router.navigate([currentUrl]);
        });
    }
}
